/* eslint-disable */
"use client";

import { useRouter } from "./router";
import { useState, useTransition } from "react";
import { HandCoins, Search, Loader2, Plus, X, Wallet, BadgeCheck, Pencil } from "lucide-react";
import type { employees, loans } from "./types";
import {
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  Select,
  StatusPill,
  TextInput,
  Textarea,
} from "./ui";
import { PageHeader } from "./sidebar";
import { useUrlFilters } from "./router";
import { toast } from "sonner";
import { createLoan, LOAN_DEDUCT_TO_OPTIONS, LOAN_TYPES, updateLoan, type LoanInput } from "./api";
import { cn, formatDate, formatPeso } from "./utils";
import { RollingNumber } from "./rolling-number";

type Row = { loan: typeof loans.$inferSelect; employee: typeof employees.$inferSelect };
type EmployeeOption = { id: number; firstName: string; lastName: string; employeeCode: string };
const MAX_LOAN_AMOUNT = 10_000_000;
const MAX_REPAYMENT_TERMS = 240;
const MAX_INTEREST_RATE = 100;

function getDefaultLoanForm(): LoanInput {
  const today = new Date().toISOString().slice(0, 10);
  return {
    employeeId: "",
    loanType: LOAN_TYPES[0],
    principal: "",
    perPeriodDeduction: "",
    deductTo: "Payroll",
    startDate: today,
    disbursementDate: today,
    repaymentTerms: "",
    interestRate: "0",
    remarks: "",
  };
}

function getLoanFormFromRow(row: Row): LoanInput {
  return {
    employeeId: String(row.loan.employeeId ?? ""),
    loanType: row.loan.loanType || LOAN_TYPES[0],
    principal: String(row.loan.principal ?? ""),
    perPeriodDeduction: String(row.loan.perPeriodDeduction ?? ""),
    deductTo: row.loan.deductTo || "Payroll",
    startDate: row.loan.startDate || row.loan.disbursementDate || new Date().toISOString().slice(0, 10),
    disbursementDate: row.loan.disbursementDate || row.loan.startDate || new Date().toISOString().slice(0, 10),
    repaymentTerms: String(row.loan.repaymentTerms ?? ""),
    interestRate: String(row.loan.interestRate ?? "0"),
    remarks: row.loan.remarks || "",
  };
}

export function getAutoDeductionPerPayout(principalRaw: string, repaymentTermsRaw: string) {
  const principalText = principalRaw.trim();
  const repaymentTermsText = repaymentTermsRaw.trim();
  if (!principalText && !repaymentTermsText) {
    return { value: "", error: "" };
  }

  const principal = Number(principalText);
  if (!principalText || !Number.isFinite(principal) || principal <= 0) {
    return {
      value: "",
      error: "Enter a valid positive loan amount to calculate the deduction per payout.",
    };
  }

  const repaymentTerms = Number(repaymentTermsText);
  if (
    !repaymentTermsText
    || !Number.isFinite(repaymentTerms)
    || !Number.isInteger(repaymentTerms)
    || repaymentTerms <= 0
  ) {
    return {
      value: "",
      error: "Enter valid repayment terms greater than zero to calculate the deduction per payout.",
    };
  }

  return { value: (principal / repaymentTerms).toFixed(2), error: "" };
}

export function canEditLoanRecords(user: any) {
  if (!user) return false;
  if (user.is_super_admin) return true;
  return user.role === "admin" && Boolean(user.permissions?.payroll_access);
}

export function validateLoanForm(form: LoanInput) {
  const principal = Number(form.principal);
  const perPeriod = Number(form.perPeriodDeduction);
  const repaymentTerms = Number(form.repaymentTerms);
  const interestRate = Number(form.interestRate);

  if (!form.employeeId) return "Please select an employee.";
  if (!form.loanType.trim()) return "Loan type is required.";
  if (!Number.isFinite(principal) || principal <= 0) return "Loan amount must be a positive amount.";
  if (principal > MAX_LOAN_AMOUNT) return "Loan amount looks too large. Please double-check the amount.";
  if (!form.disbursementDate) return "Disbursement date is required.";
  if (!form.startDate) return "Repayment start date is required.";
  if (!Number.isInteger(repaymentTerms) || repaymentTerms <= 0 || repaymentTerms > MAX_REPAYMENT_TERMS) {
    return `Repayment terms must be between 1 and ${MAX_REPAYMENT_TERMS} payouts.`;
  }
  if (!LOAN_DEDUCT_TO_OPTIONS.includes((form.deductTo || "") as (typeof LOAN_DEDUCT_TO_OPTIONS)[number])) {
    return "Deduct to must be Payroll or Mobility Fund.";
  }
  const autoDeduction = getAutoDeductionPerPayout(form.principal, form.repaymentTerms);
  if (autoDeduction.error) return autoDeduction.error;
  if (!Number.isFinite(perPeriod) || perPeriod <= 0) return "Deduction per payout must be a positive amount.";
  if (!Number.isFinite(interestRate) || interestRate < 0 || interestRate > MAX_INTEREST_RATE) {
    return "Interest rate must be between 0 and 100.";
  }
  return "";
}

function getStoredPayoraUser() {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function LoanModal({
  activeEmployees,
  mode,
  row,
  onClose,
}: {
  activeEmployees: EmployeeOption[];
  mode: "create" | "edit";
  row?: Row | null;
  onClose: () => void;
}) {
  const router = useRouter();
  const isEdit = mode === "edit" && row;
  const [form, setForm] = useState<LoanInput>(() => (row ? getLoanFormFromRow(row) : getDefaultLoanForm()));
  const [error, setError] = useState("");
  const [pending, startTransition] = useTransition();
  const set = (key: keyof LoanInput, value: string) => setForm((f) => ({ ...f, [key]: value }));
  const autoDeductionState = getAutoDeductionPerPayout(form.principal, form.repaymentTerms);
  const effectivePerPeriodDeduction = autoDeductionState.value || form.perPeriodDeduction;

  const principal = Number(form.principal) || 0;
  const perPeriod = Number(effectivePerPeriodDeduction) || 0;
  const interestRate = Number(form.interestRate) || 0;
  const repaymentTerms = Number(form.repaymentTerms) || 0;
  const payouts = perPeriod > 0 ? Math.ceil(principal / perPeriod) : 0;

  const submit = () => {
    setError("");
    const submitForm = { ...form, perPeriodDeduction: effectivePerPeriodDeduction };
    const validationError = validateLoanForm(submitForm);
    if (validationError) {
      setError(validationError);
      toast.error(validationError);
      return;
    }
    if (isEdit && !window.confirm("Save changes to this loan record?")) {
      return;
    }
    startTransition(() => {
      void (async () => {
        const res = isEdit
          ? await updateLoan(row.loan.id, {
              ...submitForm,
              expectedUpdatedAt: row.loan.updatedAt || row.loan.createdAt || "",
            })
          : await createLoan(submitForm);
        if (!res.ok) {
          setError(res.error);
          toast.error(res.error || `Unable to ${isEdit ? "update" : "record"} loan.`);
          return;
        }
        toast.success(isEdit ? "Loan record updated." : "Loan recorded successfully.");
        router.refresh();
        onClose();
      })();
    });
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? "Edit loan record" : "Record employee loan"}
      subtitle={
        isEdit
          ? "Update the saved loan details. Changes are tracked in the payroll audit history."
          : "The deduction is automatically collected every payroll run until the balance is fully paid."
      }
      icon={<HandCoins className="h-5 w-5" />}
      fullscreen
      hideBackdrop
      panelClassName="h-full max-w-none"
      contentClassName="flex min-h-0 flex-1 flex-col px-5 py-4 sm:px-6 sm:py-5"
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <Field label="Employee" className="xl:col-span-2">
          <Select value={form.employeeId} onChange={(e) => set("employeeId", e.target.value)} autoFocus>
            <option value="">Select an active employee&hellip;</option>
            {activeEmployees.map((e) => (
              <option key={e.id} value={e.id}>
                {e.lastName}, {e.firstName} &mdash; {e.employeeCode}
              </option>
            ))}
          </Select>
          </Field>
          <Field label="Loan type">
            <Select value={form.loanType} onChange={(e) => set("loanType", e.target.value)}>
              {[...LOAN_TYPES, "Other"].map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </Select>
          </Field>
          <Field label="Disbursement date">
            <TextInput type="date" value={form.disbursementDate} onChange={(e) => set("disbursementDate", e.target.value)} />
          </Field>
          <Field label="Repayment start">
            <TextInput type="date" value={form.startDate} onChange={(e) => set("startDate", e.target.value)} />
          </Field>
          <Field label="Loan amount">
            <div className="relative">
              <span className="absolute top-1/2 left-3.5 -translate-y-1/2 text-sm font-semibold text-faint">&#8369;</span>
              <TextInput
                className="pl-8"
                inputMode="decimal"
                placeholder="10,000.00"
                value={form.principal}
                onChange={(e) => set("principal", e.target.value)}
              />
            </div>
          </Field>
          <Field label="Repayment terms">
            <TextInput
              inputMode="numeric"
              placeholder="12"
              value={form.repaymentTerms}
              onChange={(e) => set("repaymentTerms", e.target.value)}
            />
          </Field>
          <Field label="Interest rate">
            <div className="relative">
              <TextInput
                className="pr-8"
                inputMode="decimal"
                placeholder="0"
                value={form.interestRate}
                onChange={(e) => set("interestRate", e.target.value)}
              />
              <span className="absolute top-1/2 right-3.5 -translate-y-1/2 text-sm font-semibold text-faint">%</span>
            </div>
          </Field>
          <div className="grid grid-cols-1 gap-4 xl:col-span-2 sm:grid-cols-2">
            <Field label="Deduction per payout">
              <div className="relative">
                <span className="absolute top-1/2 left-3.5 -translate-y-1/2 text-sm font-semibold text-faint">&#8369;</span>
                <TextInput
                  className="pl-8"
                  inputMode="decimal"
                  placeholder="500.00"
                  value={effectivePerPeriodDeduction}
                  readOnly
                />
              </div>
              {autoDeductionState.error && (
                <p className="mt-2 text-xs font-semibold text-danger">{autoDeductionState.error}</p>
              )}
            </Field>
            <Field label="Deduct to">
              <Select value={form.deductTo} onChange={(e) => set("deductTo", e.target.value)}>
                {LOAN_DEDUCT_TO_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </Select>
            </Field>
          </div>
          <Field label="Remarks" hint="optional" className="xl:col-span-2">
            <Textarea
              rows={4}
              placeholder="e.g. Approved via memo #042, payable over 20 cutoffs"
              value={form.remarks}
              onChange={(e) => set("remarks", e.target.value)}
            />
          </Field>
        </div>

        {payouts > 0 && principal > 0 && (
          <div className="mt-4 flex items-center gap-3 rounded-xl bg-surface2 px-4 py-3">
            <Wallet className="h-4 w-4 shrink-0 text-accent" />
            <p className="text-xs leading-relaxed text-muted">
              Fully paid in <span className="font-bold text-text">{payouts} payout{payouts === 1 ? "" : "s"}</span>
              {" "}({formatPeso(perPeriod)} deductible per {form.deductTo === "Mobility Fund" ? "mobility fund" : "payroll"} run{repaymentTerms > 0 ? `, ${repaymentTerms} terms saved` : ""}{interestRate > 0 ? `, ${interestRate.toFixed(2)}% interest` : ""}).
            </p>
          </div>
        )}

        {error && (
          <p className="mt-4 rounded-xl border border-[color-mix(in_oklab,var(--danger)_35%,transparent)] bg-[color-mix(in_oklab,var(--danger)_10%,transparent)] px-3.5 py-2.5 text-xs font-semibold text-danger">
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2.5 border-t border-border pt-4">
          <Button variant="ghost" onClick={onClose} disabled={pending}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={pending || !activeEmployees.length}>
            {pending && <Loader2 className="h-4 w-4 animate-spin" />} {isEdit ? "Save changes" : "Record loan"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function BalanceBar({ principal, balance }: { principal: number; balance: number }) {
  const paidPct = principal > 0 ? Math.min(100, ((principal - balance) / principal) * 100) : 0;
  return (
    <div className="w-36">
      <div className="mb-1 flex items-baseline justify-between text-[11px] font-semibold">
        <span className="text-text">{formatPeso(balance, { decimals: 0 })} left</span>
        <span className="text-faint">{paidPct.toFixed(0)}%</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-[var(--chart-track)]">
        <div
          className="h-full rounded-full bg-gradient-to-r from-primary to-accent transition-all duration-500"
          style={{ width: `${Math.max(2, paidPct)}%` }}
        />
      </div>
    </div>
  );
}

export function LoansClient({
  rows,
  loanTypes,
  activeEmployees,
  stats,
  initialFilters,
  canEditLoans,
}: {
  rows: Row[];
  loanTypes: string[];
  activeEmployees: EmployeeOption[];
  stats: { activeCount: number; activeBalance: string; paidCount: number; totalPrincipal: string };
  initialFilters: { q: string; status: string; type: string };
  canEditLoans?: boolean;
}) {
  const { values, set, reset } = useUrlFilters(initialFilters);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<Row | null>(null);
  const hasFilters = values.q || values.status || values.type;
  const canManageLoans = (typeof canEditLoans === "boolean" ? canEditLoans : true) && canEditLoanRecords(getStoredPayoraUser());

  return (
    <>
      <PageHeader
        eyebrow="Receivables"
        title="Loan records"
        description="Track employee loans and cash advances. Balances are deducted automatically each payroll run."
        actions={
          canManageLoans ? (
          <Button variant="accent" onClick={() => setCreateModalOpen(true)} disabled={!activeEmployees.length}>
            <Plus className="h-4 w-4" /> New loan
          </Button>
          ) : undefined
        }
      />

      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[
          { label: "Outstanding balance", value: formatPeso(Number(stats.activeBalance)), sub: `${stats.activeCount} active loan${stats.activeCount === 1 ? "" : "s"}` },
          { label: "Principal released", value: formatPeso(Number(stats.totalPrincipal)), sub: "Lifetime total across all loans" },
          { label: "Loans fully paid", value: String(stats.paidCount), sub: "Settled through payroll deductions" },
        ].map((s) => (
          <Card key={s.label} className="rise-in p-5">
            <p className="text-[11px] font-bold tracking-[0.14em] text-faint uppercase">{s.label}</p>
            <RollingNumber text={s.value} className="font-display mt-2 text-2xl font-bold tracking-tight text-text" />
            <p className="mt-1.5 text-xs font-medium text-muted">{s.sub}</p>
          </Card>
        ))}
      </div>

      <div className="mb-4 rounded-[18px] p-px shadow-[0_0_0_1px_color-mix(in_oklab,var(--border-strong)_35%,transparent),var(--depth-1)]">
        <Card className="rise-in p-3.5">
          <div className="grid grid-cols-2 gap-2.5 md:grid-cols-[1fr_180px_200px_auto]">
            <div className="relative col-span-2 md:col-span-1">
              <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-faint" />
              <TextInput
                className="pl-9"
                placeholder="Search employee name or code\u2026"
                value={values.q}
                onChange={(e) => set("q", e.target.value)}
              />
            </div>
            <Select value={values.status} onChange={(e) => set("status", e.target.value)}>
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="paid">Fully Paid</option>
            </Select>
            <Select value={values.type} onChange={(e) => set("type", e.target.value)}>
              <option value="">All loan types</option>
              {[...new Set([...LOAN_TYPES, ...loanTypes])].map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </Select>
            {hasFilters ? (
              <Button variant="ghost" onClick={reset}>
                <X className="h-4 w-4" /> Clear
              </Button>
            ) : (
              <div className="hidden items-center px-2 text-[11px] font-semibold text-faint md:flex">{rows.length} shown</div>
            )}
          </div>
        </Card>
      </div>

      <Card className="rise-in overflow-hidden">
        {rows.length === 0 ? (
          <EmptyState
            icon={<HandCoins className="h-6 w-6" />}
            title="No loan records"
            description={
              hasFilters
                ? "No loans match your current filters."
                : "Record a loan and Payora will collect it automatically through payroll deductions."
            }
            action={
              !hasFilters && activeEmployees.length && canManageLoans ? (
                <Button variant="accent" onClick={() => setCreateModalOpen(true)}>
                  <Plus className="h-4 w-4" /> New loan
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] text-sm">
              <thead>
                <tr className="border-b border-border bg-surface2 text-left text-[10px] font-bold tracking-[0.12em] text-faint uppercase">
                  <th className="px-5 py-3 sm:px-6">Employee</th>
                  <th className="px-4 py-3">Loan</th>
                  <th className="px-4 py-3 text-right">Principal</th>
                  <th className="px-4 py-3">Balance</th>
                  <th className="px-4 py-3 text-right">Per payout</th>
                  <th className="px-4 py-3">Start</th>
                  <th className="px-4 py-3">Status</th>
                  {canManageLoans && <th className="px-5 py-3 text-right sm:px-6">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map(({ loan, employee }) => (
                  <tr key={loan.id} className="border-b border-border/60 transition-colors last:border-0 hover:bg-surface2/70">
                    <td className="px-5 py-3.5 sm:px-6">
                      <p className="font-semibold text-text">
                        {employee.firstName} {employee.lastName}
                      </p>
                      <p className="text-[11px] font-medium text-faint">{employee.employeeCode}</p>
                    </td>
                    <td className="px-4 py-3.5">
                      <p className="font-medium text-text">{loan.loanType}</p>
                      {loan.remarks && (
                        <p className="mt-0.5 max-w-52 truncate text-[11px] text-faint" title={loan.remarks}>
                          {loan.remarks}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-right font-semibold whitespace-nowrap text-muted">
                      {formatPeso(Number(loan.principal))}
                    </td>
                    <td className="px-4 py-3.5">
                      {loan.status === "paid" ? (
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-success">
                          <BadgeCheck className="h-4 w-4" /> Settled
                        </span>
                      ) : (
                        <BalanceBar principal={Number(loan.principal)} balance={Number(loan.balance)} />
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-right font-display font-bold whitespace-nowrap text-text">
                      {formatPeso(Number(loan.perPeriodDeduction))}
                    </td>
                    <td className="px-4 py-3.5 text-xs font-medium whitespace-nowrap text-muted">{formatDate(loan.startDate)}</td>
                    <td className="px-4 py-3.5"><StatusPill status={loan.status} /></td>
                    {canManageLoans && (
                      <td className="px-5 py-3.5 text-right sm:px-6">
                        <Button variant="ghost" size="sm" onClick={() => setEditingRow({ loan, employee })}>
                          <Pencil className="h-3.5 w-3.5" /> Edit
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {createModalOpen && <LoanModal activeEmployees={activeEmployees} mode="create" onClose={() => setCreateModalOpen(false)} />}
      {editingRow && <LoanModal activeEmployees={activeEmployees} mode="edit" row={editingRow} onClose={() => setEditingRow(null)} />}
    </>
  );
}
